import { mat2d } from 'gl-matrix';
import {
    BaseCompositor,
    type BlendMode,
    type ClipSoftness,
    type CompModel,
    computeRenderAccuracyPercentage,
    createEmptyRenderAccuracy,
    Dimension,
    type RenderAccuracy,
    equals,
    frameIndexToHTMLSeekTime,
    getGradientCordsByAngle,
    GroupCompElement,
    IAssetsLoader,
    ImageAsset,
    ImageCompElement,
    Mask,
    MaskModeTypes,
    Position,
    Region,
    RegionTypes,
    ShapeCompElement,
    SvgCompElement,
    VideoAsset,
    VideoCompElement,
    type VisualCompElement,
} from '@bynder-studio/render-core';
import { BorderAlignmentTypes, type ColorParams, Gradient, StrokeType, toRGBA } from '@bynder-studio/misc';
import { getRadialGradientMeasurements, RadialGradientSize } from '@bynder-studio/render-core/src/Helpers/utils';
import { CanvasCompLayer } from './CanvasCompLayer';

type GradientOptions = {
    region: Region;
    radialSize: RadialGradientSize;
};

export class CanvasLayerCompositor extends BaseCompositor {
    private offscreenCanvas: HTMLCanvasElement;

    canvasWrapper: HTMLElement;

    canvas: HTMLCanvasElement;

    ctx: CanvasRenderingContext2D;

    private drawCompModelRequestId = 0;

    useFrameBufferForVideoAsset = false;

    devicePixelRatio: number;

    protected draggingId: number = null;

    protected baseLayer: CanvasCompLayer | null = null;

    private readonly trackedAssets = new Set<string>();

    private readonly decorationPathCache = new Map<string, Path2D>();

    constructor(canvasWrapperEl: HTMLElement, assetLoader: IAssetsLoader, dpr = 1) {
        super(assetLoader);

        this.canvasWrapper = canvasWrapperEl;
        this.canvas = document.createElement('canvas');
        this.canvas.tabIndex = 1;
        this.canvas.style.outline = 'none';
        this.canvasWrapper.appendChild(this.canvas);
        this.devicePixelRatio = dpr;
        this.ctx = this.canvas.getContext('2d');
    }

    static getPositionOnUnderlyingLayer(ctx: CanvasRenderingContext2D, absPosition: Position) {
        const m = ctx.getTransform().inverse();

        const temp = {
            x: absPosition.x * m.a + absPosition.y * m.c + m.e,
            y: absPosition.x * m.b + absPosition.y * m.d + m.f,
        };

        return {
            x: absPosition.x - temp.x,
            y: absPosition.y - temp.y,
        };
    }

    getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }

    setScale(scale: number) {
        this.scale = this.devicePixelRatio * scale;
    }

    getScaleTest() {
        return this.scale;
    }

    getScale(): number {
        return this.scale / this.devicePixelRatio;
    }

    getDpr(): number {
        return this.devicePixelRatio;
    }

    setPanPosition(panPosition: Position) {}

    setDraggingId(draggingId) {
        this.draggingId = draggingId;
    }

    getPanPosition(): Position {
        return new Position(0, 0);
    }

    getPanRegion(): Region {
        const { x, y } = this.getPanPosition();

        if (x === 0 && y === 0) {
            return new Region(0, 0, this.roi.getWidth(), this.roi.getHeight());
        }

        const diffWidth = (this.roi.getWidth() * this.scale - this.canvas.width) / 2;
        const diffHeight = (this.roi.getHeight() * this.scale - this.canvas.height) / 2;

        const leftX = (diffWidth - x) / this.scale;
        const topY = (diffHeight - y) / this.scale;

        return new Region(leftX, topY, leftX + this.canvas.width / this.scale, topY + this.canvas.height / this.scale);
    }

    setCompositorDimension(dimension: Dimension) {
        this.canvas.width = this.devicePixelRatio * dimension.getWidth();
        this.canvas.height = this.devicePixelRatio * dimension.getHeight();
        this.canvas.style.width = dimension.getWidth() + 'px';
        this.canvas.style.height = dimension.getHeight() + 'px';
    }

    getCompositorDimension(): Dimension {
        const { width, height } = this.canvas.style;

        return new Dimension(parseInt(width, 10), parseInt(height, 10));
    }

    getCompositorWrapperDimension(): Dimension {
        const { width, height } = this.canvasWrapper.getBoundingClientRect();

        return new Dimension(Math.trunc(width), Math.trunc(height));
    }

    s(n: number): number {
        // shorthand scale function, apply to everything related to coordinates
        return n * this.scale;
    }

    requestToDrawCompModel(compModel: CompModel): void {
        if (this.drawCompModelRequestId) {
            cancelAnimationFrame(this.drawCompModelRequestId);
        }

        this.drawCompModelRequestId = requestAnimationFrame(() => {
            this.drawCompModelRequestId = 0;
            this.drawCompModel(compModel);
        });
    }

    createBaseLayer(compModel: CompModel): CanvasCompLayer {
        this.roi = new Region(0, 0, compModel.getDimension().getWidth(), compModel.getDimension().getHeight());

        return new CanvasCompLayer(null, 'root', this.roi, this.roi, this.canvas, this.s(1));
    }

    createElementLayer(
        el: VisualCompElement,
        type: RegionTypes = RegionTypes.TRANSFORMED,
        allowCrop = true,
    ): CanvasCompLayer {
        const region = el.getRegion(type);

        if (region.getWidth() < 1 || region.getHeight() < 1) {
            return null;
        }

        const absRegion = el.getAbsoluteRegion(type);
        const layerName = el.id + '-' + type;
        let croppedRegion = region;

        // NOTE: Quick fix for turn off cropping for animations that demand scaling
        const check = (el: VisualCompElement) =>
            el?.parent?.scale !== 1 ? true : el?.parent?.parent ? check(el.parent.parent) : false;
        const hasAnimatedParent = el.parent ? check(el) : false;

        if (!this.debugNoLayerCrop && allowCrop && !hasAnimatedParent) {
            const pan = this.getPanRegion();
            const tempRegion = new Region(
                Math.max(region.left, pan.left - absRegion.left + region.left),
                Math.max(region.top, pan.top - absRegion.top + region.top),
                Math.min(region.right, pan.right - absRegion.right + region.right),
                Math.min(region.bottom, pan.bottom - absRegion.bottom + region.bottom),
            );

            // NOTE: Extend the drawing region to include the drop shadow
            tempRegion.applyDropShadow(el.dropShadow);

            if (tempRegion.getArea() < croppedRegion.getArea()) {
                croppedRegion = tempRegion;
            }
        }

        if (croppedRegion.getWidth() < 1 || croppedRegion.getHeight() < 1) {
            return null;
        }

        const layer = new CanvasCompLayer(el, layerName, croppedRegion, absRegion, null, this.s(1));

        BaseCompositor.layerAreaPxCreated += layer.getWidth() * layer.getHeight();
        BaseCompositor.layerAreasCreated += 1;

        if (this.debug) {
            super.printDebugInfo(
                `Created effect layer ${layer.id} with size (${layer.canvas.width},${
                    layer.canvas.height
                }) at region (${layer.region.toArray()})`,
            );
        }

        // only translate to the content
        layer.applyTranslation(-croppedRegion.left, -croppedRegion.top);

        return layer;
    }

    getDataUrl(): string {
        return this.canvas.toDataURL();
    }

    /**
     *
     * @param el Element to draw including transformation and effects
     * @param layer Layer transformed to content origin
     * @param force Force drawing even if element is not visible
     */
    drawAtomicElementOnLayer(el: VisualCompElement, layer: CanvasCompLayer, force = false) {
        const hasOverflowEffects = el.hasOverflowEffects();
        const drawOnBaseLayer = !hasOverflowEffects && !el.requiresIntermediateLayer();

        if (drawOnBaseLayer) {
            // if element has no mask, and no overflow effects, no intermediate layers are necessary
            if (el.rotation !== 0 || el.scale !== 1) {
                this.applyTransform(el.getScaleAndRotationTransform(), layer.context);
            }

            this.applyClipPath(el.clipPath, layer.context, 1);

            const opacity = this.draggingId === el.id ? el.opacity * 0.8 : el.opacity;
            this.applyOpacity(opacity, layer.context);
            this.applyBlendMode(el.blendMode, layer);

            // finally draw the content
            this.drawContentOnLayer(el, layer, force);

            this.applyClipSoftness(el.clipSoftness, layer.context);

            // Avoid unnecessary debug string construction and function calls when debug mode is off
            if (this.debug) {
                this.printDebugInfo(
                    `Drawing element ${el.id} onto layer ${layer.id} at position (${layer.getTransform()})`,
                );
            }
        } else {
            // Effects require intermediate layers to be created
            let tmpLayer = this.createElementLayer(el);

            if (!tmpLayer) {
                return;
            }

            // We draw the baseLayer's region of the groupCompElement on the tempLayer
            // if some of the children have blend mode, otherwise double layer creation
            // prevents child's blend mode normal work
            this.drawCopiedLayerArea(el as GroupCompElement, this.baseLayer, tmpLayer);

            if (el.rotation !== 0 || el.scale !== 1) {
                this.applyTransform(el.getScaleAndRotationTransform(), tmpLayer.context);
            }

            this.applyClipPath(el.clipPath, tmpLayer.context, 1);

            this.drawContentOnLayer(el, tmpLayer, force);

            this.applyMask(el.mask, tmpLayer);

            this.applyClipSoftness(el.clipSoftness, tmpLayer.context);

            if (hasOverflowEffects) {
                // if overflow effects are present, first precomp onto temporary layer
                const effectLayer = this.createElementLayer(el, RegionTypes.EFFECT);

                if (!effectLayer) {
                    return;
                }

                // apply effects
                this.applyBlur(el.blur, effectLayer.context);
                this.applyDropShadow(el.dropShadow, 0, effectLayer.context);

                // draw contentLayer with effects
                this.mergeLayers(tmpLayer, effectLayer);

                tmpLayer = effectLayer;
            }

            this.applyOpacity(el.opacity, layer.context);
            this.applyBlendMode(el.blendMode, layer);

            // draw contentLayer with effects
            this.mergeLayers(tmpLayer, layer);

            this.printDebugInfo(
                `Drawing layer ${tmpLayer.id} onto layer ${layer.id} with size (${tmpLayer.canvas.width},${
                    tmpLayer.canvas.height
                }) at position (${layer.getTransform()})`,
            );

            this.displayDebugLayer(tmpLayer);
        }
    }

    drawCopiedLayerArea(el: GroupCompElement, copiedLayer: CanvasCompLayer, tmpLayer: CanvasCompLayer) {
        if (!el.requireUnderlyingLayerCopying()) {
            return;
        }

        const { x, y } = CanvasLayerCompositor.getPositionOnUnderlyingLayer(
            copiedLayer.context as CanvasRenderingContext2D,
            el.getAbsContentPosition(),
        );

        // Rounding is not enough here
        const w = this.s(el.contentBox.dimension.getWidth()) + 1;
        const h = this.s(el.contentBox.dimension.getHeight()) + 1;

        tmpLayer.context.drawImage(copiedLayer.canvas, x, y, w, h, 0, 0, w, h);
    }

    drawContentOnLayer(el: VisualCompElement, layer: CanvasCompLayer, force = false) {
        if (el instanceof SvgCompElement || el instanceof ShapeCompElement) {
            this.drawSvgCompElement(el, layer);
        } else if (el instanceof VideoCompElement) {
            this.drawVideoCompElement(el, layer);
        } else if (el instanceof ImageCompElement) {
            this.drawImageCompElement(el, layer);
        } else if (el instanceof GroupCompElement) {
            el.getChildren().forEach((childEl) => {
                layer.save();
                layer.applyTranslation(
                    childEl.boundingBox.position.x + childEl.contentBox.position.x,
                    childEl.boundingBox.position.y + childEl.contentBox.position.y,
                );

                this.drawElementOnLayer(childEl, layer, force);

                layer.restore();
            });
        }
    }

    private mergeLineDecorationPaths(lineEl: GroupCompElement): void {
        // Early-exit check without allocating any filtered arrays.
        // Short-circuits at the first decorated letter — O(1) for the common non-decorated case.
        let hasAnyDecoration = false;
        outer: for (const word of lineEl.getChildren()) {
            if (!this.isElementVisible(word)) continue;

            for (const letter of (word as GroupCompElement).getChildren()) {
                if (this.isElementVisible(letter) && (letter as SvgCompElement).decorationPaths?.length > 0) {
                    hasAnyDecoration = true;
                    break outer;
                }
            }
        }

        if (!hasAnyDecoration) {
            return;
        }

        // Build filtered arrays only when there are decorations to process.
        const visibleWords = lineEl.getChildren().filter((e) => this.isElementVisible(e)) as GroupCompElement[];
        const wordLetters = visibleWords.map((word) =>
            (word.getChildren() as SvgCompElement[]).filter((e) => this.isElementVisible(e)),
        );

        // Direct field comparison — eliminates two 18-field object allocations + one deep-equals call per letter.
        // The scale check mirrors the original NaN trick: two letters with non-unit scale are always "different".
        const sameLetterProps = (
            a: SvgCompElement,
            wa: GroupCompElement,
            b: SvgCompElement,
            wb: GroupCompElement,
        ): boolean =>
            a.opacity === b.opacity &&
            a.rotation === b.rotation &&
            a.scale === b.scale &&
            a.scale === 1 &&
            a.blur === b.blur &&
            a.blendMode === b.blendMode &&
            a.strokeType === b.strokeType &&
            a.strokeWidth === b.strokeWidth &&
            a.horizontalPathScale === b.horizontalPathScale &&
            a.translatedX === b.translatedX &&
            a.translatedY === b.translatedY &&
            wa.opacity === wb.opacity &&
            wa.rotation === wb.rotation &&
            wa.scale === wb.scale &&
            wa.scale === 1 &&
            wa.blur === wb.blur &&
            wa.blendMode === wb.blendMode &&
            wa.translatedX === wb.translatedX &&
            wa.translatedY === wb.translatedY &&
            (a.clipPath === b.clipPath || equals(a.clipPath, b.clipPath)) &&
            (a.color === b.color || equals(a.color, b.color)) &&
            (wa.clipPath === wb.clipPath || equals(wa.clipPath, wb.clipPath));

        const toPath2D = (path: string | Path2D): Path2D => {
            if (typeof path !== 'string') {
                return path;
            }

            let cached = this.decorationPathCache.get(path);

            if (!cached) {
                cached = new Path2D(path);
                this.decorationPathCache.set(path, cached);
            }

            return cached;
        };

        // Single DOMMatrix reused across all addPath calls — only .e (translateX) is mutated per call.
        const matrix = new DOMMatrix();

        const getNextLetter = (
            wordIndex: number,
            letterIndex: number,
        ): { letter: SvgCompElement; word: GroupCompElement } | null => {
            const letters = wordLetters[wordIndex];

            if (letters && letterIndex + 1 < letters.length) {
                return { letter: letters[letterIndex + 1], word: visibleWords[wordIndex] };
            }

            for (let wi = wordIndex + 1; wi < wordLetters.length; wi++) {
                if (wordLetters[wi].length > 0) {
                    return { letter: wordLetters[wi][0], word: visibleWords[wi] };
                }
            }

            return null;
        };

        let decorationPath = new Path2D();
        let hasAccumulated = false;

        visibleWords.forEach((word, wordIndex) => {
            const letters = wordLetters[wordIndex];
            const x = word.boundingBox.position.x + word.contentBox.position.x;

            letters.forEach((letter, i) => {
                const letterPaths = letter.decorationPaths;
                const hasDecoration = letterPaths?.length > 0;

                if (!hasDecoration && !hasAccumulated) {
                    return;
                }

                const lx =
                    (x + letter.boundingBox.position.x + letter.contentBox.position.x + letter.pathPosition.x) /
                    letter.horizontalPathScale;

                if (hasDecoration) {
                    matrix.e = lx;

                    for (const path of letterPaths) {
                        decorationPath.addPath(toPath2D(path), matrix);
                    }

                    letter.decorationPaths = [];
                    hasAccumulated = true;
                }

                const next = getNextLetter(wordIndex, i);
                const isDifferentProps = !next || !sameLetterProps(letter, word, next.letter, next.word);

                if (isDifferentProps) {
                    const decoration = new Path2D();
                    matrix.e = -lx;
                    decoration.addPath(decorationPath, matrix);
                    letter.decorationPaths = [decoration as unknown as string];
                    decorationPath = new Path2D();
                    hasAccumulated = false;
                }
            });
        });
    }

    protected preprocessCompModel(compModel: CompModel): void {
        const walkGroup = (group: GroupCompElement) => {
            for (const child of group.getChildren()) {
                if (!(child instanceof GroupCompElement)) {
                    continue;
                }

                if (child.name?.startsWith('line_') && this.isElementVisible(child)) {
                    this.mergeLineDecorationPaths(child);
                } else {
                    walkGroup(child);
                }
            }
        };

        for (const el of compModel.getCompVisualElements()) {
            if (el instanceof GroupCompElement) {
                walkGroup(el);
            }
        }
    }

    applyMask(mask: Mask, layer: CanvasCompLayer) {
        if (!mask) {
            return;
        }

        if (mask.mode === MaskModeTypes.ALPHA_INVERTED || mask.mode === MaskModeTypes.ALPHA) {
            const maskLayer = this.getMaskLayer(String(mask.elementId)) as CanvasCompLayer;

            if (!maskLayer) {
                return;
            }

            if (!maskLayer.absoluteRegion.getIntersection(layer.absoluteRegion)) {
                if (mask.mode === MaskModeTypes.ALPHA) {
                    layer.clear();
                }

                return;
            }

            const ctx = layer.context;
            layer.save();

            if (mask.mode === MaskModeTypes.ALPHA_INVERTED) {
                ctx.globalCompositeOperation = 'destination-out';
            } else if (mask.mode === MaskModeTypes.ALPHA) {
                // normal
                ctx.globalCompositeOperation = 'destination-in';
            }

            // transform from masked element content back to main layer origin
            let transform = mat2d.invert(mat2d.create(), layer.el.getAbsContentTransform());

            // tranform to mask element parent from origin
            if (maskLayer.el.parent) {
                transform = mat2d.multiply(transform, transform, maskLayer.el.parent.getAbsContentTransform());
            }

            // since scaling and rotation were applied to the masklayer, we only need to translate to mask content origin
            // additionally we correct for layer effects
            transform = mat2d.translate(transform, transform, [
                maskLayer.el.boundingBox.position.x + maskLayer.el.contentBox.position.x + maskLayer.region.left,
                maskLayer.el.boundingBox.position.y + maskLayer.el.contentBox.position.y + maskLayer.region.top,
            ]);

            this.applyTransform(transform, ctx);

            ctx.drawImage(maskLayer.canvas, 0, 0);

            this.displayDebugLayer(maskLayer);

            ctx.globalCompositeOperation = 'source-over';

            layer.restore();
        }
    }

    private drawImageCompElement(el: ImageCompElement, layer: CanvasCompLayer) {
        const asset = this.assetLoader.getAsset<ImageAsset>(el.assetId);

        this.trackAssetAccuracy('images', el.assetId, asset);

        if (!asset || !asset.object || !asset.isAccessible()) {
            // this.drawPlaceholderRectangle(el.contentBox, el.boundingBox);
            return;
        }

        const { object, width, height } = asset;
        layer
            .getContext()
            .drawImage(
                object as HTMLImageElement,
                el.cropPositionPct.getX() * width,
                el.cropPositionPct.getY() * height,
                el.cropDimensionPct.getWidth() * width,
                el.cropDimensionPct.getHeight() * height,
                0,
                0,
                this.s(el.contentBox.dimension.getWidth()),
                this.s(el.contentBox.dimension.getHeight()),
            );
    }

    private drawVideoCompElement(el: VideoCompElement, layer: CanvasCompLayer) {
        const asset = this.assetLoader.getAsset(el.assetId) as VideoAsset;

        this.trackAssetAccuracy('videos', el.assetId, asset);

        if (!asset || !asset.object || !asset.isAccessible()) {
            // this.drawPlaceholderRectangle(el.contentBox, el.boundingBox);
            return;
        }

        const seekTime = frameIndexToHTMLSeekTime(el.frameIndex, asset.frameRate);
        const key = parseFloat(seekTime.toFixed(2));

        this.trackFrameSeekingAccuracy(el.assetId, asset, key);

        const videoFrame = asset.frameBuffer.get(key);
        const objectToDraw =
            this.useFrameBufferForVideoAsset && videoFrame
                ? videoFrame
                : asset.isAlpha
                  ? this.getAlphaVideoFrame(asset.object)
                  : asset.object;
        const videoWidth = asset.object.videoWidth;
        const videoHeight = !asset.isAlpha ? asset.object.videoHeight : asset.object.videoHeight / 2;

        if (!videoWidth || !videoHeight) {
            return;
        }

        layer
            .getContext()
            .drawImage(
                objectToDraw,
                el.cropPositionPct.getX() * videoWidth,
                el.cropPositionPct.getY() * videoHeight,
                el.cropDimensionPct.getWidth() * videoWidth,
                el.cropDimensionPct.getHeight() * videoHeight,
                0,
                0,
                this.s(el.contentBox.dimension.getWidth()),
                this.s(el.contentBox.dimension.getHeight()),
            );
    }

    private drawSvgCompElement(el: SvgCompElement | ShapeCompElement, layer: CanvasCompLayer) {
        if (el?.glyph?.fontId) {
            const fontId = String(el.glyph.fontId);
            this.trackAssetAccuracy('fonts', fontId, this.assetLoader.getAsset(fontId));
        }

        const ctx = layer.getContext();

        ctx.save();

        if (el instanceof ShapeCompElement && el.borderWidth) {
            this.drawShapeWithBorder(el, layer);
        } else {
            this.drawAnySvg(el, layer, new Path2D(el.path));
            el.decorationPaths?.forEach((path) => this.drawAnySvg(el, layer, new Path2D(path)));
        }

        ctx.restore();
    }

    private getGradientOptions(el: SvgCompElement | ShapeCompElement): GradientOptions {
        return {
            region: el.getRegion(RegionTypes.UNTRANSFORMED),
            radialSize: el.radialGradientSize,
        };
    }

    fillOrStrokePath(
        ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
        path: Path2D,
        operation: 'fill' | 'stroke',
        colorType: ColorParams['type'],
        width: number,
        height: number,
        dx = 0,
        dy = 0,
    ) {
        if (colorType === 'SOLID' || colorType !== 'RADIAL_GRADIENT' || width === height) {
            ctx[operation](path);

            return;
        }

        if (operation === 'stroke') {
            ctx.strokeStyle = this.generateGradientPattern(ctx, width, height);
            const matrix = new DOMMatrix().translate(dx, dy);
            ctx.strokeStyle.setTransform(matrix);
            ctx.stroke(path);

            return;
        }

        ctx.save();
        // Clip to the existing path so that the fill only affects the path area.
        ctx.clip(path);

        // Save the context before applying the scale transformation.
        ctx.save();

        let scaleX = 1;
        let scaleY = 1;

        // Transform the gradient to match the element's content box.
        if (width > height) {
            scaleX = width / height;
            ctx.translate(width / 2 - (width * scaleX) / 2, 0);
        } else {
            scaleY = height / width;
            ctx.translate(0, height / 2 - (height * scaleY) / 2);
        }

        ctx.scale(scaleX, scaleY);

        if (operation === 'fill') {
            ctx.fillRect(0, 0, width, height);
        } else {
            ctx.strokeRect(0, 0, width, height);
        }

        // Restore the scaling transformation.
        ctx.restore();
        // Remove the clipping region.
        ctx.restore();
    }

    drawAnySvg(el: SvgCompElement, layer: CanvasCompLayer, path: Path2D) {
        const ctx = layer.getContext();

        ctx.save();
        ctx.scale(this.s(1), this.s(1));
        ctx.translate(el.pathPosition.getX(), el.pathPosition.getY());
        ctx.scale(el.horizontalPathScale, el.verticalPathScale);

        const strokeScale = 1 / el.horizontalPathScale;
        const strokeWidth = el.strokeWidth * strokeScale;

        // Temporary solution for fix and stroke in transcript.
        // TODO: Implement in scope of this task for every type of element with text
        // https://bynder.atlassian.net/browse/VBS-25662
        if (el.strokeColor && el.fillColor) {
            this.applyColor(el.fillColor, ctx, () => this.getGradientOptions(el), 'fillStyle');
            this.fillOrStrokePath(
                ctx,
                path,
                'fill',
                el.fillColor.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
            );
        }

        this.applyColor(el.color, ctx, () => this.getGradientOptions(el));

        if (el.strokeType === StrokeType.INSIDE) {
            ctx.lineWidth = strokeWidth * 2;
            ctx.clip(path);
            ctx.stroke(path);
        } else if (el.strokeType === StrokeType.OUTSIDE) {
            ctx.lineWidth = strokeWidth * 2;
            ctx.stroke(path);

            ctx.globalCompositeOperation = 'destination-out';
            ctx.globalAlpha = 1;
            ctx.fillStyle = 'rgba(0, 0, 0, 1)';
            ctx.fill(path);
        } else if (el.strokeType === StrokeType.CENTER) {
            ctx.lineWidth = strokeWidth;
            ctx.stroke(path);
        } else {
            this.fillOrStrokePath(
                ctx,
                path,
                'fill',
                el.color.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
            );
        }

        ctx.closePath();
        ctx.restore();
    }

    /**
     *
     * @param el
     * @param layer
     */
    private drawShapeWithBorder(el: ShapeCompElement, layer: CanvasCompLayer) {
        const p = new Path2D(el.path);
        const ctx = layer.getContext();
        const lineWidth = el.borderWidth;
        const lineCap = el.borderLineCap;
        const borderAlignment = el.borderAlignment;
        const fillColor = el.color;
        const strokeColor = el.borderColor;

        ctx.save();

        // Correct scale of the paths by setting the scale on the canvas
        // Apply local SVG translate and scale(first translate then scale)
        // NOTE That since we already scaled the canvas, we should not scale pathPosition and pathScale again
        ctx.scale(this.s(1), this.s(1));
        ctx.translate(el.pathPosition.getX(), el.pathPosition.getY());
        ctx.scale(el.horizontalPathScale, el.verticalPathScale);

        if (lineCap) {
            ctx.lineCap = lineCap;
        }

        // draw shape on offscreen canvas
        if (borderAlignment === BorderAlignmentTypes.INSIDE) {
            const isSolidColor = fillColor.type === 'SOLID' && strokeColor.type === 'SOLID';
            const isOpacityEqual = isSolidColor && fillColor.opacity === strokeColor.opacity;

            if (!this.currentCompModel.isElementMaskEnabled(el.id) || !isOpacityEqual) {
                /* Since we cannot disable anti-aliasing fully on a canvas context,
                we only apply an alternative way of drawing for the inside border type */
                ctx.clip(p);

                // draw fill
                ctx.lineWidth = lineWidth * 2;
                this.applyColor(fillColor, ctx, () => this.getGradientOptions(el), 'fillStyle');
                this.fillOrStrokePath(
                    ctx,
                    p,
                    'fill',
                    fillColor.type,
                    el.contentBox.dimension.getWidth(),
                    el.contentBox.dimension.getHeight(),
                );

                ctx.save();

                ctx.globalAlpha = 1;
                // remove border area from fill
                ctx.globalCompositeOperation = 'destination-out';
                ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
                ctx.stroke(p);

                ctx.restore();

                // re-draw border
                ctx.globalCompositeOperation = 'source-over';
                this.applyColor(strokeColor, ctx, () => this.getGradientOptions(el), 'strokeStyle');
                this.fillOrStrokePath(
                    ctx,
                    p,
                    'stroke',
                    strokeColor.type,
                    el.contentBox.dimension.getWidth(),
                    el.contentBox.dimension.getHeight(),
                    -el.pathPosition.getX(),
                    -el.pathPosition.getY(),
                );
            } else {
                // To improve quality in case of a mask with the same opacity for fill and stroke, we use an alternative routine
                ctx.fillStyle = toRGBA(fillColor);
                ctx.fill(p);
            }
        } else if (borderAlignment === BorderAlignmentTypes.MIDDLE) {
            // draw fill
            ctx.lineWidth = lineWidth;
            this.applyColor(fillColor, ctx, () => this.getGradientOptions(el), 'fillStyle');
            this.fillOrStrokePath(
                ctx,
                p,
                'fill',
                fillColor.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
            );

            // remove border area from fill, but at first we get rid of opacity if it has transparency
            ctx.save();

            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'destination-out';
            ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
            ctx.stroke(p);

            ctx.restore();

            // re-draw border
            ctx.globalCompositeOperation = 'source-over';
            this.applyColor(strokeColor, ctx, () => this.getGradientOptions(el), 'strokeStyle');
            this.fillOrStrokePath(
                ctx,
                p,
                'stroke',
                strokeColor.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
                -el.pathPosition.getX(),
                -el.pathPosition.getY(),
            );
        } else if (borderAlignment === BorderAlignmentTypes.OUTSIDE) {
            // draw border
            ctx.lineWidth = lineWidth * 2;
            this.applyColor(strokeColor, ctx, () => this.getGradientOptions(el), 'strokeStyle');
            this.fillOrStrokePath(
                ctx,
                p,
                'stroke',
                strokeColor.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
                -el.pathPosition.getX(),
                -el.pathPosition.getY(),
            );

            // remove fill but at first we get rid of opacity if it has transparency
            ctx.save();

            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fillStyle = 'rgba(0, 0, 0, 1)';
            ctx.fill(p);

            ctx.restore();

            // re-draw fill
            ctx.globalCompositeOperation = 'source-over';
            this.applyColor(fillColor, ctx, () => this.getGradientOptions(el), 'fillStyle');
            this.fillOrStrokePath(
                ctx,
                p,
                'fill',
                fillColor.type,
                el.contentBox.dimension.getWidth(),
                el.contentBox.dimension.getHeight(),
            );
        }

        ctx.restore();
    }

    mergeLayers(sourceLayer: CanvasCompLayer, targetLayer: CanvasCompLayer): void {
        if (!targetLayer.canvas.width || !targetLayer.canvas.height) {
            return;
        }

        targetLayer.context.drawImage(
            sourceLayer.canvas,
            this.s(sourceLayer.region.left),
            this.s(sourceLayer.region.top),
        );
    }

    // TODO: remove;
    clearFrame() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    generateGradient(
        ctx: CanvasCompLayer['context'],
        { type, stops, angle }: Gradient,
        { region, radialSize: radialDistance }: GradientOptions,
    ) {
        let gradient;

        if (type === 'LINEAR_GRADIENT') {
            const { x0, y0, x1, y1 } = getGradientCordsByAngle(angle, region);
            gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        } else {
            const { cx, cy, r } = getRadialGradientMeasurements(region, radialDistance);
            gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        }

        [...stops]
            .sort((a, b) => a.offset - b.offset)
            .forEach((stop) => {
                gradient.addColorStop(stop.offset, toRGBA(stop));
            });

        return gradient;
    }

    generateGradientPattern(ctx: CanvasCompLayer['context'], width: number, height: number) {
        const patternCanvas = document.createElement('canvas');
        const patternCtx = patternCanvas.getContext('2d');

        patternCanvas.width = width;
        patternCanvas.height = height;

        let scaleX = 1;
        let scaleY = 1;

        if (width > height) {
            scaleX = width / height;
            scaleY = 1;
        } else {
            scaleX = 1;
            scaleY = height / width;
        }

        const cx = width / 2;
        const cy = height / 2;

        patternCtx.save();
        patternCtx.translate(cx, cy); // move origin to center
        patternCtx.scale(scaleX, scaleY); // scale around center
        patternCtx.translate(-cx, -cy); // move origin back

        patternCtx.fillStyle = ctx.strokeStyle;
        patternCtx.fillRect(0, 0, width, height);

        patternCtx.restore();

        return ctx.createPattern(patternCanvas, 'no-repeat');
    }

    applyColor(
        color: ColorParams,
        ctx: CanvasCompLayer['context'],
        getOptions: () => GradientOptions,
        style?: 'fillStyle' | 'strokeStyle',
    ) {
        const c = color.type === 'SOLID' ? toRGBA(color) : this.generateGradient(ctx, color, getOptions());

        if (style !== 'fillStyle') {
            ctx.strokeStyle = c;
        }

        if (style !== 'strokeStyle') {
            ctx.fillStyle = c;
        }
    }

    applyOpacity(opacity, ctx) {
        if (opacity != 1) {
            ctx.globalAlpha *= opacity;
        }
    }

    applyBlendMode(blendMode: BlendMode, layer: CanvasCompLayer) {
        layer.setCompositeOperation(blendMode?.getCompositeOperation() ?? 'source-over');
    }

    /**
     * Apply transform to a layer
     * @param transform
     * @param ctx
     */
    applyTransform(transform, ctx) {
        const s = this.s(1);
        ctx.transform(transform[0], transform[1], transform[2], transform[3], transform[4] * s, transform[5] * s);
    }

    applyRotation(deltaX, deltaY, rotation, ctx) {
        if (rotation % 360 !== 0) {
            ctx.translate(this.s(deltaX), this.s(deltaY));
            ctx.rotate((Math.PI / 180) * rotation);
            ctx.translate(-this.s(deltaX), -this.s(deltaY));
        }
    }

    applyScale(deltaX, deltaY, scale, ctx) {
        if (scale !== 1) {
            ctx.translate(this.s(deltaX), this.s(deltaY));
            ctx.scale(scale, scale);
        }
    }

    applyBlur(blur, ctx) {
        if (blur !== 0) {
            ctx.filter = `blur(${blur * 100}px)`;
        }
    }

    applyDropShadow(dropShadow, counterRotation, ctx) {
        if (dropShadow && dropShadow.state !== 'DISABLE') {
            const color = dropShadow.color;
            ctx.shadowColor = `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.opacity})`;
            ctx.shadowBlur = this.s(dropShadow.blurRadius);

            if (counterRotation) {
                const [dsOffsetX, dsOffsetY] = this.rotatePoint(
                    counterRotation,
                    dropShadow.offsetX,
                    dropShadow.offsetY,
                );
                ctx.shadowOffsetX = this.s(dsOffsetX);
                ctx.shadowOffsetY = this.s(dsOffsetY);
            } else {
                ctx.shadowOffsetX = this.s(dropShadow.offsetX);
                ctx.shadowOffsetY = this.s(dropShadow.offsetY);
            }
        }
    }

    applyBackgroundColor(color: ColorParams, ctx: CanvasCompLayer['context']) {
        ctx.save();

        const dimension = this.currentCompModel.getDimension();
        const width = this.s(dimension.getWidth());
        const height = this.s(dimension.getHeight());

        this.applyColor(color, ctx, () => ({
            region: new Region(0, 0, width, height),
            radialSize: 'outcircle',
        }));

        const rectPath = new Path2D();
        rectPath.rect(0, 0, width, height);

        this.fillOrStrokePath(ctx, rectPath, 'fill', color.type, width, height);
        ctx.restore();
    }

    /**
     * Rotate coordinate around center point
     * https://stackoverflow.com/questions/17410809/how-to-calculate-rotation-in-2d-in-javascript
     */
    rotatePoint(angle, x, y, cx = 0, cy = 0) {
        if (angle % 360 !== 0) {
            const radians = (Math.PI / 180) * angle;
            const cos = Math.cos(radians);
            const sin = Math.sin(radians);
            const nx = cos * (x - cx) + sin * (y - cy) + cx;
            const ny = cos * (y - cy) - sin * (x - cx) + cy;

            return [nx, ny];
        }

        return [x, y];
    }

    /**
     * Apply clippath to the canvas.
     * Does not save and restore, which would erase the path
     * @param path
     * @param ctx
     * @param scale
     */
    applyClipPath(path, ctx, scale = 1) {
        if (!path) {
            return;
        }

        const transform = ctx.getTransform();
        // ctx.resetTransform();
        // draw clipPath at the scale of the canvas clipPath
        ctx.scale(this.s(scale), this.s(scale)); // set scale for path
        ctx.beginPath();
        ctx.clip(new Path2D(path));
        ctx.setTransform(transform);
    }

    applyClipSoftness(clipSoftness: ClipSoftness | undefined, ctx, scale = 1) {
        if (!clipSoftness) {
            return;
        }

        const transform = ctx.getTransform();
        ctx.scale(this.s(scale), this.s(scale)); // set scale for path
        ctx.beginPath();
        const { x0, y0, x1, y1 } = clipSoftness.gradient;
        const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)'); // Start with transparent
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)'); // End with black

        const { x, y, w, h, angle, dx = 0, dy = 0 } = clipSoftness.rectangle;

        if (angle) {
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.translate(-x, -y);
        }

        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = gradient;
        ctx.fillRect(x + dx, y + dy, w, h);
        ctx.globalCompositeOperation = 'source-over';
        ctx.setTransform(transform);
    }

    drawCompModel(compModel: CompModel): void {
        this.currentCompModel = compModel;
        this.printDebugInfo(['Drawing compmodel frame: ', compModel.getFrame()]);
        this.printDebugInfo(compModel);

        this.trackedAssets.clear();
        this.lastDrawAccuracy = createEmptyRenderAccuracy();

        const startTime = performance.now();

        this.prepareDraw(compModel);
        this.preprocessCompModel(compModel);

        if (this.baseLayer) {
            this.baseLayer.resetTransform();
            this.baseLayer.clear(null);
        }

        this.drawCheckerboardPattern(this.ctx);

        this.applyBackgroundColor(compModel.getBackgroundColor(), this.ctx);

        // TODO: Added filtering by only one id for testing!!
        compModel.getCompVisualElements().forEach((el, idx) => {
            this.drawCompElement(el);
        });

        this.computeAccuracyPercentage();

        this.cleanupDraw();

        const endTime = performance.now();

        this.printDebugInfo(
            `Drew ${BaseCompositor.layerAreasCreated} areas with ${(
                BaseCompositor.layerAreaPxCreated / 1000000
            ).toFixed(2)} Megapixels`,
        );
        this.printDebugInfo(
            `Drew frame ${compModel.getFrame()} in ${(endTime - startTime).toFixed(2)} ms \
                    scale ${this.scale.toFixed(2)}`,
        );
    }

    protected drawCheckerboardPattern(ctx) {
        const orgTransform = ctx.getTransform();
        ctx.resetTransform();
        ctx.save();

        const size = this.devicePixelRatio * 6;
        const w = ctx.canvas.width;
        const h = ctx.canvas.height;
        const numX = Math.ceil(w / size);
        const numY = Math.ceil(h / size);

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);

        ctx.fillStyle = '#EDEEF0';

        for (let x = 0; x < numX; x++) {
            for (let y = 0; y < numY; y++) {
                if ((x + y) % 2 === 0) {
                    ctx.fillRect(x * size, y * size, size, size);
                }
            }
        }

        ctx.restore();
        ctx.setTransform(orgTransform);
    }

    private trackAssetAccuracy(
        category: keyof RenderAccuracy['assetLoading'],
        assetId: string,
        asset: { isAccessible(): boolean } | undefined,
    ) {
        const key = `${category}:${assetId}`;

        if (this.trackedAssets.has(key)) {
            return;
        }

        this.trackedAssets.add(key);
        const counts = this.lastDrawAccuracy.assetLoading[category];
        counts.total++;

        if (asset?.isAccessible()) {
            counts.accurate++;
        }
    }

    private trackFrameSeekingAccuracy(assetId: string, asset: VideoAsset, seekKey: number) {
        if (!this.useFrameBufferForVideoAsset) {
            return;
        }

        const key = `seek:${assetId}`;

        if (this.trackedAssets.has(key)) {
            return;
        }

        this.trackedAssets.add(key);
        const { videos } = this.lastDrawAccuracy.frameSeeking;
        videos.total++;

        if (asset.frameBuffer.has(seekKey)) {
            videos.accurate++;
        }
    }

    private computeAccuracyPercentage() {
        this.lastDrawAccuracy.percentage = computeRenderAccuracyPercentage(this.lastDrawAccuracy);
    }

    private getAlphaVideoFrame(videoObject: HTMLVideoElement) {
        // calculations for alpha/rgb images mixing
        if (!this.offscreenCanvas) {
            this.offscreenCanvas = document.createElement('canvas');
        }

        this.offscreenCanvas.width = videoObject.videoWidth;
        this.offscreenCanvas.height = videoObject.videoHeight;
        const videoCanvasCtx = this.offscreenCanvas.getContext('2d', { willReadFrequently: true });
        videoCanvasCtx.drawImage(videoObject, 0, 0, videoObject.videoWidth, videoObject.videoHeight);
        const imageDataRgb = videoCanvasCtx.getImageData(
            0,
            0,
            this.offscreenCanvas.width,
            this.offscreenCanvas.height / 2,
        );
        const imageDataAlpha = videoCanvasCtx.getImageData(
            0,
            this.offscreenCanvas.height / 2,
            this.offscreenCanvas.width,
            this.offscreenCanvas.height / 2,
        );
        const imageDataRgbData = imageDataRgb.data;
        const imageDataAlphaData = imageDataAlpha.data;

        // update every fourth pixel from the RGB image with transparency from the alpha image (0 - 255)
        for (let i = 3; i < imageDataRgbData.length; i += 4) {
            imageDataRgbData[i] = Math.floor(
                (imageDataAlphaData[i - 1] + imageDataAlphaData[i - 2] + imageDataAlphaData[i - 3]) / 3,
            );
        }

        // restore canvas height to correct dimension
        this.offscreenCanvas.height = this.offscreenCanvas.height / 2;
        videoCanvasCtx.putImageData(imageDataRgb, 0, 0);

        return this.offscreenCanvas;
    }

    /**
     * Draw overlay for non-groups based on region of interest
     * @param el
     */
    private drawDebugCompElementRoiOverlay(el) {
        if (el instanceof GroupCompElement) {
            el.getChildren().forEach((childEl) => {
                this.drawDebugCompElementRoiOverlay(childEl);
            });
        } else {
            const region = el.getAbsoluteRegion();
            const ctx = this.ctx;
            ctx.save();

            ctx.globalAlpha = 0.1;
            ctx.lineWidth = 2;
            ctx.fillStyle = '#f00';

            ctx.beginPath();
            ctx.rect(this.s(region.left), this.s(region.top), this.s(region.getWidth()), this.s(region.getHeight()));
            ctx.fill();
            ctx.closePath();

            ctx.restore();
        }
    }

    private displayDebugLayer(layer) {
        if (this.debug) {
            layer.canvas.convertToBlob().then((blob) => {
                const divEl = document.createElement('div');
                divEl.textContent = layer.id;
                document.body.appendChild(divEl);
                const imageEl = document.createElement('img');
                imageEl.src = window.URL.createObjectURL(blob);
                imageEl.style.border = '3px solid red';
                document.body.appendChild(imageEl);
                document.body.appendChild(document.createElement('br'));
            });
        }
    }
}
